import dayjs from "dayjs";

import { createClientFilter } from "../../client";
import * as api from "../../services/api";
import * as f from "../../utils/formatter";

const doClientSearch = async (signal, term, location_id) => {
  let filter = createClientFilter(term);
  if (filter == null) {
    return [];
  }

  if (location_id != null) {
    filter = { and: [filter, { location_id: { eq: location_id } }] };
  }

  const res = await api.clientSearch(
    {
      filter: filter,
      page: { limit: 10, offset: 0 },
    },
    { signal },
  );

  return res.data.clients.data;
};

async function fetchSuggestions(signal, filter) {
  const suggestions = await api.appointmentSuggestions(
    {
      filter: filter,
    },
    { signal: signal },
  );
  return suggestions.data.appointmentSuggestions.data.map((r) => {
    return {
      id: `${r.start}.${r.end}.${r.calendar_id}`,
      ...r,
    };
  });
}

/**
 * @param {AbortSignal} signal
 */
const fetchProductOptions = async (signal, filter) => {
  if (filter == null) {
    return [];
  }

  const res = await api.productOptions(
    {
      filter: filter,
    },
    { signal },
  );

  return res.data.products.data;
};

/**
 * @param {AbortSignal} signal
 */
const fetchServiceOptions = async (signal, filter) => {
  if (filter == null) {
    return [];
  }

  const res = await api.locationServiceOptions(
    {
      filter: filter,
    },
    { signal },
  );

  return res.data.locationServices.data.map((ls) => ({
    id: ls.service.id,
    duration: ls.duration,
    name: `${ls.service.name} (${f.minutesToDuration(ls.duration)})`,
    requires_doctor: ls.requires_doctor,
  }));
};

/**
 * @param {AbortSignal} signal
 */
async function fetchLocationOptions(signal) {
  const res = await api.locationOptions({ signal });

  return res.data.locations.data;
}

const fetchEmployees = async (signal, filter) => {
  const res = await api.availableEmployees(
    {
      filter,
      order: { asc: "last_name" },
    },
    { signal },
  );
  return res.data.employees.data;
};

const loadAppointment = async (appointment_id, location_id) => {
  const res = await api.loadAppointments({
    filter: {
      id: { eq: appointment_id },
    },
    location_id,
  });
  return res.data.appointments.data;
};

const loadAppointments = async (client_id, location_id) => {
  const res = await api.loadAppointments({
    filter: {
      start: { gte: "2015-01-01" },
      client_id: { eq: client_id },
    },
    order: {
      desc: "start",
    },
    location_id,
  });
  return res.data.appointments.data;
};

/**
 * Extended appointment date generation with weekday and time filtering
 * @param {number} generationRound - Current generation round (1-based) - used to expand search range
 * @param {Array} calendarRange - Array with start and end dates [startDate, endDate] in ISO format
 * @param {Array} selectedWeekdays - Array of selected weekday numbers (0=Sunday, 1=Monday, ..., 6=Saturday)
 * @param {Array} selectedTimeParts - Array of selected time parts: ['morning', 'beforenoon', 'noon', 'afternoon', 'evening'] (optional if using custom times)
 * @param {number} customTimeFrom - Custom start hour (0-23), overrides selectedTimeParts if provided
 * @param {number} customTimeTo - Custom end hour (0-23), overrides selectedTimeParts if provided
 * @param {number} order_from_h - Number of hours you can order to from current time
 * @returns {Array<{from: string, to: string}>} Array of date range objects
 */
const generateAppointmentDates = (
  generationRound,
  calendarRange,
  selectedWeekdays = [],
  selectedTimeParts = [],
  customTimeFrom = null,
  customTimeTo = null,
  order_from_h,
) => {
  const result = [];
  // Use calendarRange as base, but expand based on generation round
  const baseStartDate = new Date(calendarRange[0]);
  const baseEndDate = new Date(calendarRange[1]);

  // Expand the search range based on generation round
  const additionalWeeks = generationRound - 1;
  const expandedStartDate = new Date(baseStartDate);
  expandedStartDate.setDate(baseStartDate.getDate() + additionalWeeks * 7);

  // Expand end date as well to maintain the same range duration
  const expandedEndDate = new Date(baseEndDate);
  expandedEndDate.setDate(baseEndDate.getDate() + additionalWeeks * 7);

  // Determine time ranges to use
  let timeRangesToUse = [];

  if (selectedTimeParts.filter(Boolean).length > 1) {
    // Convert selected time parts to time ranges
    timeRangesToUse = convertTimePartsToRanges(selectedTimeParts);
  } else {
    timeRangesToUse = [{ startHour: customTimeFrom, endHour: customTimeTo }];
  }

  // Calculate the number of days to iterate through
  const timeDifference =
    expandedEndDate.getTime() - expandedStartDate.getTime();
  const daysToGenerate = Math.ceil(timeDifference / (1000 * 60 * 60 * 24)) + 1;

  // Get today's date for comparison
  const today = new Date();
  const todayDateString = today.toDateString();

  for (let i = 0; i < daysToGenerate; i++) {
    const currentDate = new Date(expandedStartDate);
    currentDate.setDate(expandedStartDate.getDate() + i);

    // Check if current date is actually today
    const isFirstDay = currentDate.toDateString() === todayDateString;
    if (calendarRange[0] && calendarRange[1]) {
      if (!isFirstDay) {
        calendarRange[0] = calendarRange[0].hour(0);
      }
      calendarRange[1] = calendarRange[1].hour(23);
    }

    const weekday = currentDate.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday

    // Skip if this weekday is not selected
    if (
      !selectedWeekdays[weekday - 1] &&
      selectedWeekdays.filter(Boolean).length > 0
    ) {
      continue;
    }

    // Generate date ranges for each time range
    for (const timeRange of timeRangesToUse) {
      const dateRange = createCustomTimeRange(
        currentDate,
        timeRange,
        isFirstDay, // True only when current date is actually today
        order_from_h,
      );
      if (dateRange) {
        result.push(dateRange);
      }
    }
  }

  return result;
};

/**
 * Creates a date range for a custom time range
 * @param {Date} date - The date to create range for
 * @param {Object} timeRange - Time range with startHour and endHour
 * @param {boolean} isToday - Whether this is today (for time adjustment)
 * @returns {Object|null} Date range object or null if invalid
 */
const createCustomTimeRange = (date, timeRange, isToday, order_from_h) => {
  const { startHour, endHour } = timeRange;

  // Set start time
  const fromDate = new Date(date);
  let actualStartHour = startHour;

  if (isToday) {
    // For today, respect the 5-hour buffer if it affects this time range
    const currentHour = new Date().getHours();
    const minStartHour = currentHour + order_from_h;

    // If the entire time range is in the past, skip it
    if (minStartHour >= endHour) {
      return null;
    }

    // Adjust start hour if needed
    actualStartHour = Math.max(startHour, minStartHour);
  }

  fromDate.setHours(actualStartHour, 0, 0, 0);

  // Set end time
  const toDate = new Date(date);
  toDate.setHours(endHour, 0, 0, 0);

  return {
    from: f.dateTimeInput(fromDate),
    to: f.dateTimeInput(toDate),
  };
};

/**
 * Converts selected time parts to time ranges
 * @param {Array} selectedTimeParts - Array of selected time part names
 * @returns {Array} Array of time range objects with startHour and endHour
 */
const convertTimePartsToRanges = (selectedTimeParts) => {
  const timePartDefinitions = [
    { startHour: 6, endHour: 10 },
    { startHour: 9, endHour: 12 },
    { startHour: 11, endHour: 13 },
    { startHour: 12, endHour: 17 },
    { startHour: 16, endHour: 22 },
  ];
  const selectedTimePartDefinitions = [];
  selectedTimeParts.forEach((value, index) => {
    if (value) {
      selectedTimePartDefinitions.push(timePartDefinitions[index]);
    }
  });
  return selectedTimePartDefinitions;
};

/**
 * Converts hour number to time string for display
 * @param {number} hour - Hour (0-23)
 * @returns {string} Time string (e.g., "09:00", "14:00")
 */
const hourToTimeString = (hour) => {
  return `${hour.toString().padStart(2, "0")}:00`;
};

/**
 * Converts time string to hour number
 * @param {string} timeString - Time string (e.g., "09:00", "14:00")
 * @returns {number} Hour as integer (e.g., 9, 14)
 */
const timeStringToHour = (timeString) =>
  timeString ? parseInt(timeString.split(":")[0], 10) : null;

/**
 * Helper function to get all time part options for checkboxes
 * @returns {Array} Array of time part objects
 */
const getTimePartOptions = () => {
  return [
    "Ráno (6:00 - 10:00)",
    "Dopoledne (9:00 - 12:00)",
    "Poledne (11:00 - 13:00)",
    "Odpoledne (12:00 - 17:00)",
    "Večer (16:00 - 22:00)",
  ];
};

/**
 * Checks if can go back to previous page
 * @param {Object} state - Current state object
 * @returns {boolean} Whether can go back
 */
const canGoBack = (state) => {
  return state.currentPageIndex > 0;
};

const getStartDateFromNote = (note) => {
  const reservationUnitOptions = {
    earliestAvailableDate: 1,
    days: 1,
    weeks: 7,
    months: 28,
  };
  const unit = note.reservation_unit;
  const count = note.reservation_count;
  return dayjs().add(count * reservationUnitOptions[unit], "day");
};

// Deterministic pagination: sorted by start asc, chunked.
function stablePaginateSuggestions(all, perPage = 5) {
  const arr = Array.isArray(all) ? [...all] : [];
  arr.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const pages = [];
  for (let i = 0; i < arr.length; i += perPage)
    pages.push(arr.slice(i, i + perPage));
  return pages;
}

export {
  loadAppointment,
  loadAppointments,
  doClientSearch,
  fetchSuggestions,
  fetchEmployees,
  fetchProductOptions,
  fetchServiceOptions,
  fetchLocationOptions,
  generateAppointmentDates,
  timeStringToHour,
  hourToTimeString,
  getTimePartOptions,
  canGoBack,
  getStartDateFromNote,
  stablePaginateSuggestions,
};
